<template>
  <div class="container-fluid-full">
    <div class="container-fluid">
      <div class="row">
        <div class="col-md-4">
          <div class="page-title m-b-none">
            <h1>Run Sheet Details</h1>
          </div>
        </div>
        <div class="col-md-8 text-md-right">
          <div class="run-sheet-actions">
            <button
              type="button"
              class="btn btn-info"
              data-toggle="modal"
              data-target="#modal-scan-into-run"
            >
              <font-awesome :icon="['far', 'scanner-touchscreen']"/>
              Add to run
            </button>
            &nbsp;
            <template v-if="isCourier">
              <async-button
                type="button"
                class="btn btn-info m-r-sm"
                :click="handleDownload"
              >
                <font-awesome :icon="['far', 'print']" /> Delivery Note
              </async-button>
              <async-button
                type="button"
                class="btn btn-success"
                :click="complete"
                v-if="deliveryRun.editable"
              >
                <font-awesome :icon="['far', 'plus']"/>
                Complete Delivery Run
              </async-button>
            </template>
            <template v-else>
              <async-button
                type="button"
                class="btn btn-blue btn-reoptimise"
                :click="reoptimise"
              >
                <font-awesome :icon="['far', 'random']"/>
                Re-Optimise
              </async-button>
              &nbsp;
              <router-link
                v-if="deliveryRun.started"
                type="button"
                class="btn btn-blue"
                :to="{ name: 'delivery-runs.run-sheet.stop', params: { id: deliveryRun.id } }"
              >
                <font-awesome :icon="['far', 'road']"/>
                Driver View
              </router-link>
              <async-button
                v-else
                type="button"
                class="btn btn-success"
                :click="start"
              >
                <font-awesome :icon="['far', 'plus']"/>
                Start Run
              </async-button>
              &nbsp;
              <async-button type="button" class="btn btn-success" :click="handleDownload"> <font-awesome :icon="['far', 'print']" /> Run Sheet </async-button>
            </template>
          </div>
        </div>
      </div>
      <!-- /.row -->

      <div class="run-details">
        <div class="container-fluid">
          <div class="row">
            <div class="col-lg-4">
              <div class="form-horizontal">
                <label class="col-md-1 col-lg-2 control-label">Driver:</label>
                <div class="col-md-11 col-lg-10">
                  <input v-if="isCourier" type="text" class="form-control" value="Courier" disabled>
                  <div v-else>
                    <multiselect
                      :disabled="!deliveryRun.editable"
                      placeholder
                      hide-selected
                      preselect-first
                      :options="driverOptions"
                      v-model="driver"
                      label="name"
                      track-by="id"
                      :show-labels="false"
                    />
                  </div>
                </div>
              </div>
              <!-- /.form-horizontal-->
            </div>
            <div class="col-lg-8" v-if="!isCourier">
              <ul class="run-meta">
                <li><strong>Stops:</strong> {{ deliveryRun.stops.length }}</li>
                <li>
                  <strong>Distance:</strong>
                  {{ distanceInKilometres | nullable }}
                </li>
                <li>
                  <strong>Duration:</strong>
                  {{
                    durationInHuman
                  }}
                </li>
                <li>
                  <strong>Created at:</strong>
                  {{
                    deliveryRun.created_at
                      | date({ format: "DD/MM/YYYY h:mma", utc: false })
                  }}
                </li>
              </ul>
            </div>
          </div>
          <!-- /.row -->
        </div>
      </div>
      <!-- /.run-details -->
    </div>

    <div class="run-main">
      <div class="run-left">
        <run-sheet-stops v-if="deliveryRunExists"/>

        <div class="run-left-footer" v-if="deliveryRun.editable">
          <async-button
            type="button"
            class="btn btn-danger pull-right"
            :click="confirmBeforeDeletingDeliveryRun"
          >
            <font-awesome :icon="['far', 'trash-alt']"/>
            Delete Run Sheet
          </async-button>
        </div>
      </div>

      <div class="run-right">
        <run-sheet-map
          v-if="deliveryRunExists"
          @updated="mapUpdated"
          ref="map"
        />
      </div>
    </div>

    <add-station-to-run-modal @added="stationsAddedToDeliveryRun"/>
    <add-garment-to-run-modal :id="deliveryRun.id" @hidden="handleAddGarmentToRunClose"></add-garment-to-run-modal>
  </div>
</template>

<script>
import { get, sync, call } from 'vuex-pathify'
import AddStationToRunModal from './AddStationToRunModal'
import AddGarmentToRunModal from './AddGarmentToRunModal'
import RunSheetMap from './RunSheetMap'
import RunSheetStops from './RunSheetStops'
import swal from 'sweetalert'
import humanizeDuration from 'humanize-duration'
import moment from 'moment'
import $ from 'jquery'
import { http } from 'utils'

export default {
  components: {
    AddGarmentToRunModal,
    RunSheetMap,
    RunSheetStops,
    AddStationToRunModal
  },

  data () {
    return {
      unassignedDriver: {
        id: null,
        name: 'Unassigned'
      },
      distance: null,
      duration: null
    }
  },

  computed: {
    ...get('deliveryRuns', ['deliveryRunExists', 'deliveryRun']),
    ...get('drivers', ['drivers']),
    ...sync('deliveryRuns', {
      stateDeliveryRunDriver: 'deliveryRun@driver',
      stateDeliveryRunStops: 'deliveryRun@stops',
      triggerUpdateDeliveryRunStopOrder: 'triggers@updateDeliveryRunStopOrder',
      triggerUpdateMapDirections: 'triggers@updateMapDirections'
    }),

    distanceInKilometres () {
      return this.deliveryRun.distance ? `${Math.round(this.distance * 100) / 100}kms` : null
    },

    durationInHuman () {
      return this.duration ? humanizeDuration(this.duration, { delimiter: ' ', units: ['h', 'm'], round: true }) : null
    },

    driverOptions () {
      if (this.deliveryRun.driver) {
        return this.drivers
      }

      return [this.unassignedDriver].concat(this.drivers)
    },

    driver: {
      get () {
        return this.deliveryRun.driver || this.unassignedDriver
      },

      async set (driver) {
        const previousDriver = this.deliveryRun.driver

        this.stateDeliveryRunDriver = driver

        try {
          await this.updateDeliveryRunDriver({
            id: this.deliveryRun.id,
            driver: driver.id
          })
        } catch (error) {
          this.stateDeliveryRunDriver = previousDriver
        }
      }
    },

    isCourier () {
      return this.deliveryRun && this.deliveryRun.courier
    },

    hasIncompleteRuns () {
      return this.stateDeliveryRunStops.filter(s => !s.complete).length > 0
    }

  },

  channel (vm) {
    return 'delivery-run'
  },

  echo: {
    'DeliveryRun.Reoptimised': (payload, vm) => {
      vm.refresh()
    }
  },

  async created () {
    await this.getDrivers()
  },

  beforeDestroy () {
    this.resetDeliveryRun()
  },

  methods: {
    ...call('drivers', ['getDrivers']),
    ...call('deliveryRuns', [
      'getDeliveryRun',
      'resetDeliveryRun',
      'startDeliveryRun',
      'updateDeliveryRunDriver',
      'deleteDeliveryRun',
      'completeDeliveryRun',
      'refreshDeliveryRun'
    ]),

    async start () {
      try {
        await this.startDeliveryRun(this.deliveryRun.id)
        this.$router.push({ name: 'delivery-runs.run-sheet.stop', params: { id: this.deliveryRun.id } })
      } catch (error) {
        if (error.response && error.response.status === 400) {
          this.$notify('error', error.response.data.message)
        } else {
          this.$notify('error', 'Something went wrong when starting the delivery run.')
        }
      }
    },

    async refresh () {
      const $btn = $('.btn-reoptimise')
      $btn.text('Re-optimise')
      $btn.removeClass('disabled')
      $('.run-right').css('opacity', 1)

      try {
        await this.getDeliveryRun(this.deliveryRun.id)
        this.triggerUpdateMapDirections = true
      } catch (error) {
        if (error.response && error.response.status === 400) {
          this.$notify('error', error.response.data.message)
        } else {
          this.$notify('error', 'Something went wrong when refreshing the delivery run.')
        }
      }
    },

    async confirmBeforeDeletingDeliveryRun () {
      if (
        !await swal({
          dangerMode: true,
          text: 'Are you sure you want to delete this run sheet?',
          buttons: ['Cancel', 'Delete']
        })
      ) {
        return
      }

      try {
        await this.deleteDeliveryRun(this.deliveryRun.id)

        this.$router.push({ name: 'delivery-runs.run-sheets' })
      } catch (error) {
        this.$notify('error', 'Something went wrong when deleting the run sheet.')
      }
    },

    async complete () {
      if (!this.isCourier && this.hasIncompleteRuns) {
        await swal({
          icon: 'warning',
          dangerMode: true,
          title: 'Incomplete Stop Found',
          text: 'Please complete or delete the incomplete stop(s).'
        })
        return
      }

      try {
        await this.completeDeliveryRun(this.deliveryRun.id)
      } catch (error) {
        if (error.response && error.response.status !== 400) {
          this.$notify('error', error.response.data.message)
        } else {
          this.$notify('error', 'Something went wrong when completing the run.')
        }
      }
    },

    stationsAddedToDeliveryRun (newDeliveryRunStops) {
      this.stateDeliveryRunStops = newDeliveryRunStops
      this.triggerUpdateMapDirections = true
    },

    mapUpdated () {
      const startTime = moment(this.deliveryRun.start_time, 'h:mma')
      const endTime = moment(this.deliveryRun.end_time, 'h:mma')
      this.distance = this.deliveryRun.distance
      this.duration = endTime.diff(startTime)
    },

    async reoptimise () {
      await this.refreshDeliveryRun(this.deliveryRun.id)

      const $btn = $('.btn-reoptimise')
      $btn.text('Re-optimising..')
      $btn.addClass('disabled')
      $('.run-right').css('opacity', 0.3)
    },

    handleAddGarmentToRunClose () {
      this.getDeliveryRun(this.deliveryRun.id)
    },

    async handleDownload () {
      const response = await http.get(`delivery-runs/${this.deliveryRun.id}/download`, { responseType: 'blob' })
      const objectUrl = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = objectUrl
      link.setAttribute('download', `Delivery_Run_${this.deliveryRun.id}.pdf`)
      document.body.appendChild(link)
      link.click()
    }
  }
}
</script>
